// HubKit — dark premium primitives for the Talent/Agency settings hubs.
// Tokens mirror the Admin Command Center (HomeV2): slate-950 canvas,
// glassy slate-900 cards, gradient icon chips, cyan/teal/fuchsia accents.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, CheckCircle2, Clock3, AlertTriangle, CircleDashed, RefreshCw } from 'lucide-react';

export const ACCENTS = {
  cyan:    { grad: 'from-cyan-500/30 to-teal-500/30',    border: 'border-cyan-500/40',    text: 'text-cyan-300' },
  teal:    { grad: 'from-teal-500/30 to-emerald-500/30', border: 'border-teal-500/40',    text: 'text-teal-300' },
  purple:  { grad: 'from-fuchsia-500/30 to-purple-500/30', border: 'border-fuchsia-500/40', text: 'text-fuchsia-300' },
  pink:    { grad: 'from-pink-500/30 to-rose-500/30',    border: 'border-pink-500/40',    text: 'text-pink-300' },
  gold:    { grad: 'from-amber-500/30 to-yellow-500/30', border: 'border-amber-500/40',   text: 'text-amber-300' },
  emerald: { grad: 'from-emerald-500/30 to-teal-500/30', border: 'border-emerald-500/40', text: 'text-emerald-300' },
  rose:    { grad: 'from-rose-500/30 to-pink-500/30',    border: 'border-rose-500/40',    text: 'text-rose-300' },
  slate:   { grad: 'from-slate-500/30 to-slate-600/30',  border: 'border-slate-500/40',   text: 'text-slate-300' },
};

export const IconChip = ({ icon: Icon, accent = 'cyan', size = 'md' }) => {
  const a = ACCENTS[accent] || ACCENTS.cyan;
  const dims = size === 'lg' ? 'w-12 h-12 rounded-xl' : 'w-10 h-10 rounded-lg';
  return (
    <div className={`${dims} bg-gradient-to-br ${a.grad} border ${a.border} flex items-center justify-center shrink-0`}>
      <Icon className={`${size === 'lg' ? 'w-5.5 h-5.5 w-[22px] h-[22px]' : 'w-[18px] h-[18px]'} ${a.text}`} />
    </div>
  );
};

const TONES = {
  emerald: { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', Icon: CheckCircle2 },
  amber:   { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40',       Icon: Clock3 },
  rose:    { cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40',          Icon: AlertTriangle },
  slate:   { cls: 'bg-slate-500/15 text-slate-300 border-slate-500/40',       Icon: CircleDashed },
};

export const StatusPill = ({ tone = 'slate', label, testid }) => {
  const t = TONES[tone] || TONES.slate;
  const I = t.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${t.cls}`}
      data-testid={testid}
    >
      <I className="w-3 h-3" aria-hidden="true" />
      {label}
    </span>
  );
};

export const HubCard = ({ title, sub, action, children, testid }) => (
  <section
    className="rounded-2xl border border-slate-800/60 bg-slate-900/60 backdrop-blur-sm p-5 sm:p-6"
    data-testid={testid}
  >
    {(title || action) && (
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-bold text-slate-100 tracking-tight">{title}</h2>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        {action}
      </div>
    )}
    <div className="space-y-3">{children}</div>
  </section>
);

export const ActionRow = ({ icon, accent = 'cyan', label, sub, to, badge, progress, testid }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      data-testid={testid}
      className="group w-full flex items-center gap-3.5 p-3.5 rounded-2xl text-left
                 border border-slate-800/60 bg-slate-950/50 transition-all
                 hover:bg-slate-900/80 hover:border-slate-700 hover:shadow-lg hover:shadow-slate-950/50 hover:-translate-y-0.5
                 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70"
    >
      <IconChip icon={icon} accent={accent} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-100">{label}</span>
          {badge}
        </div>
        {sub && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{sub}</div>}
        {typeof progress === 'number' && (
          <div className="mt-2 h-1.5 w-full rounded-full bg-slate-800 overflow-hidden" aria-hidden="true">
            <div
              className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-teal-400 transition-[width] duration-500"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-300 group-hover:translate-x-0.5 transition-all shrink-0" />
    </button>
  );
};

export const StatCard = ({ icon, accent = 'cyan', label, value, badge, testid }) => (
  <div
    className="rounded-2xl border border-slate-800/60 bg-slate-950/50 p-4 flex items-center gap-3
               transition-colors hover:border-slate-700"
    data-testid={testid}
  >
    <IconChip icon={icon} accent={accent} />
    <div className="min-w-0 flex-1">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-base font-bold text-slate-100 flex items-center gap-2 flex-wrap">
        <span className="truncate">{value}</span>
        {badge}
      </div>
    </div>
  </div>
);

export const SkeletonRow = ({ testid }) => (
  <div className="animate-pulse flex items-center gap-3.5 p-3.5 rounded-2xl border border-slate-800/60 bg-slate-950/50" data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-slate-800" />
    <div className="flex-1 space-y-2">
      <div className="h-3 w-1/3 rounded bg-slate-800" />
      <div className="h-2.5 w-2/3 rounded bg-slate-800/70" />
    </div>
  </div>
);

export const ErrorState = ({ message, onRetry, testid }) => (
  <div
    className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5 text-center space-y-3"
    data-testid={testid}
  >
    <AlertTriangle className="w-6 h-6 text-rose-400 mx-auto" aria-hidden="true" />
    <p className="text-sm text-slate-300">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold
                 bg-slate-900 border border-slate-700 text-slate-100 hover:border-cyan-500/50
                 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70"
      data-testid={testid ? `${testid}-retry` : undefined}
    >
      <RefreshCw className="w-3.5 h-3.5" /> Retry
    </button>
  </div>
);

export const SummaryChip = ({ ok, label, testid }) => (
  <div
    className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-semibold transition-colors ${
      ok
        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
        : 'border-amber-500/30 bg-amber-500/10 text-amber-300'
    }`}
    data-testid={testid}
  >
    {ok
      ? <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden="true" />
      : <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />}
    <span className="leading-tight">{label}{!ok && <span className="block font-normal text-[10px] opacity-80">Needs attention</span>}</span>
  </div>
);
